/**
 * GTM wedge helpers — see deck v16 + docs/GTM_WEDGE.md.
 *
 * The active wedge is Coach + Callback Zone (+ Training Box, which is a
 * physical SKU rather than a module). Every other live module is
 * `expansion` and renders below the wedge on marketing surfaces. Pro
 * stays `invite` and is never surfaced publicly.
 *
 * Edit the wedge sentence + expansion label in one place: this file.
 */

import { MODULES, type ModuleDef, type ModuleSlug } from "@/lib/modules";

export const WEDGE_COPY = {
  /**
   * Single-sentence positioning — Source of Truth for every CastReady
   * surface (hero, OG/meta, decks, marketing emails, press kit).
   * If you change this, every public surface changes with it.
   */
  positioning: "The Actor Development Operating System.",
  /** Companion subline — names every audience the platform serves. */
  subline:
    "Connecting actors, parents, coaches, studios, talent reps, and opportunities — on one platform.",
  /** Catalog section header for wedge tier. */
  wedgeHeader: "The Three CastReady Platforms",
  wedgeSub:
    "Coach, Callback Zone, and Castings — the platforms we lead with everywhere. Pro is invite-only for verified partners.",
  /** Catalog section header for expansion tier. */
  expansionHeader: "Roadmap modules — expanding post-PMF",
  expansionSub:
    "HMU, Crew, Capture, and Draftline are built and reachable, but ranked below the wedge until we're ready to market them as primary products.",
  /** Investor moat line lifted verbatim from deck Slide 11. */
  moatLine:
    "The connective tissue between training, readiness, and real industry work.",
  /** Deck pricing bands — wedge revenue lines (Slide 9). */
  wedgePricing: {
    coach: { label: "Studio SaaS", band: "$249–$449/mo" },
    callbackZone: { label: "Callback Zone", band: "$29–$49/mo · 7-day free trial" },
    trainingBox: { label: "Training Kits", band: "$85–$159" },
  },
} as const;

/**
 * Canonical one-liner per module — pull from here on every surface (Index hub
 * grid, Cmd-K palette, marketing landings) so copy never drifts.
 * Order is intentional: Coach + Callback Zone are the wedge and appear first.
 */
export const MODULE_ONE_LINERS: Readonly<Record<string, string>> = {
  coach: "Roster, lessons, billing & parent updates.",
  callback_zone: "Daily practice, lessons & casting readiness.",
  castings: "Browse opportunities, submit & manage talent.",
  draftline: "Write, format & share scripts with actors.",
  hmu: "Book hair, makeup & wardrobe pros.",
  capture: "Portfolios, packages & talent sessions.",
  crew: "Post & discover trusted crew positions.",
  pro: "Co-branded boxes & marketing exposure for partners.",
} as const;

/**
 * Structured "what / who / why" per module — render in module cards so a
 * first-time visitor immediately understands relevance. Keep one-liners in
 * `MODULE_ONE_LINERS` as the short form; this map adds audience + problem.
 */
export const MODULE_CLARITY: Readonly<
  Record<string, { audience: string; problemSolved: string }>
> = {
  coach: {
    audience: "Coaches, studios & talent reps",
    problemSolved: "Run your studio in one place — roster, lessons, billing & parent updates.",
  },
  callback_zone: {
    audience: "Actors & parents of young performers",
    problemSolved: "Stay callback-ready with daily practice, lessons & progress tracking.",
  },
  castings: {
    audience: "Talent, agents & casting teams",
    problemSolved: "Find real opportunities and manage submissions end-to-end.",
  },
  draftline: {
    audience: "Screenwriters",
    problemSolved: "Write industry-format scripts and share them with actors.",
  },
  hmu: {
    audience: "HMU artists & performers",
    problemSolved: "Book vetted hair, makeup & wardrobe pros with youth-safe controls.",
  },
  capture: {
    audience: "Photographers & videographers",
    problemSolved: "Run your booking, packages & portfolio in one place.",
  },
  crew: {
    audience: "Producers & crew",
    problemSolved: "Post positions and discover reliable, youth-safety-certified crew.",
  },
  pro: {
    audience: "Verified industry partners (invite only)",
    problemSolved: "Collaborate on co-branded Training Kits and unlock paid marketing exposure across the CastReady ecosystem.",
  },
} as const;

export function wedgeModules(): ReadonlyArray<ModuleDef> {
  return MODULES.filter((m) => m.tier === "wedge").sort((a, b) => a.order - b.order);
}

export function expansionModules(): ReadonlyArray<ModuleDef> {
  return MODULES.filter((m) => m.tier === "expansion").sort((a, b) => a.order - b.order);
}

export function isWedgeModule(slug: ModuleSlug): boolean {
  return MODULES.some((m) => m.slug === slug && m.tier === "wedge");
}

export function isExpansionModule(slug: ModuleSlug): boolean {
  return MODULES.some((m) => m.slug === slug && m.tier === "expansion");
}